const API = "/api";

function getToken(): string | null {
  return localStorage.getItem("token");
}

function getRefreshToken(): string | null {
  return localStorage.getItem("refreshToken");
}

function setTokens(token: string, refreshToken?: string): void {
  localStorage.setItem("token", token);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
}

function clearTokens(): void {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
}

let refreshing: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  if (refreshing) return refreshing;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  refreshing = (async () => {
    try {
      const res = await fetch(`${API}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) {
        clearTokens();
        return null;
      }
      const data = await res.json() as { token: string };
      localStorage.setItem("token", data.token);
      return data.token;
    } catch {
      clearTokens();
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function api<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    if (body.code === "TOKEN_EXPIRED") {
      const newToken = await doRefresh();
      if (newToken) {
        const retryRes = await fetch(`${API}${path}`, {
          ...options,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${newToken}`,
            ...options.headers,
          },
        });
        if (!retryRes.ok) {
          const retryBody = await retryRes.json().catch(() => ({ error: retryRes.statusText }));
          throw new Error(retryBody.error || JSON.stringify(retryBody));
        }
        const retryText = await retryRes.text();
        return retryText ? JSON.parse(retryText) : ({} as T);
      }
      clearTokens();
      window.location.href = "/login";
      throw new Error("Sesión expirada");
    }
    throw new Error(body.error || JSON.stringify(body));
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || JSON.stringify(body));
  }
  const text = await res.text();
  return text ? JSON.parse(text) : ({} as T);
}

export { api, getToken, getRefreshToken, setTokens, clearTokens };
